import { isEmpty } from 'lodash'
import { ERROR_CODE, ORDER_STATUS, ORDER_SUB_STATUS } from 'types'
import getApiErrorCode from 'utils/getApiErrorCode'
import {
  ADD_TECH_COMMENTS_ACTION,
  ADD_TECH_COMMENTS_LABEL,
  SPECIAL_ACTIONS_PARTIAL,
  TECH_COMMENTS_ALLOWED_STATUSES,
} from './constants'

// Helpers for the manager order show "special actions" partials. Kept out of
// the form components so the rules can be unit tested without rendering
// Ionic/Formik.

// An in-progress work order can be moved between "Awaiting Parts" and
// "Estimate", in either direction.
export const IN_PROGRESS_STATUSES = [
  ORDER_STATUS.AWAITING_PARTS,
  ORDER_STATUS.ESTIMATE,
]

// The status an in-progress work order can be changed to: the in-progress
// status it is not currently in. Undefined for any other status.
export function getNewInProgressStatus(currentStatus) {
  if (!IN_PROGRESS_STATUSES.includes(currentStatus)) return undefined
  return IN_PROGRESS_STATUSES.find((status) => status !== currentStatus)
}

// Only the update types that apply to the work order are offered: Change
// Status and Add Tech Comments are always available, Reset ETA only when
// `canResetEta` is true.
export function getInProgressActionOptions({ canResetEta, newStatus }) {
  return [
    canResetEta && { value: 'reset_eta', label: 'Reset ETA' },
    newStatus && {
      value: 'change_status',
      label: `Change Status to ${newStatus}`,
    },
    { value: ADD_TECH_COMMENTS_ACTION, label: ADD_TECH_COMMENTS_LABEL },
  ].filter(Boolean)
}

export function canAddTechComments(orderStatus) {
  return TECH_COMMENTS_ALLOWED_STATUSES.includes(orderStatus)
}

// Which special actions partial (if any) the order show renders for a work
// order. The partials are mutually exclusive by status, so this is an ordered
// decision rather than independent flags:
// - Accept/Decline: Assigned with no sub status and no decision yet.
// - Rescheduling: Scheduled, or Assigned with an Estimate Approved sub status.
// - In Progress Update: Awaiting Parts or Estimate (any sub status).
// - Add Tech Comments (standalone): any other status that allows tech
//   comments, e.g. Tech On Site, or Assigned after an accept/decline decision.
//   The partials above already offer Add Tech Comments (or, for accept, collect
//   tech comments), so the standalone partial is only the fallback.
export function getSpecialActionsPartial(workOrder) {
  if (!workOrder) return null
  const { orderStatus, orderSubStatus, acceptDecline } = workOrder

  if (
    orderStatus === ORDER_STATUS.ASSIGNED &&
    isEmpty(orderSubStatus) &&
    isEmpty(acceptDecline)
  ) {
    return SPECIAL_ACTIONS_PARTIAL.ACCEPT_DECLINE
  }

  if (
    orderStatus === ORDER_STATUS.SCHEDULED ||
    (orderStatus === ORDER_STATUS.ASSIGNED &&
      orderSubStatus === ORDER_SUB_STATUS.ESTIMATE_APPROVED)
  ) {
    return SPECIAL_ACTIONS_PARTIAL.RESCHEDULING
  }

  if (IN_PROGRESS_STATUSES.includes(orderStatus)) {
    return SPECIAL_ACTIONS_PARTIAL.IN_PROGRESS
  }

  if (canAddTechComments(orderStatus)) {
    return SPECIAL_ACTIONS_PARTIAL.ADD_TECH_COMMENTS
  }

  return null
}

// True when the API rejected an action because the work order is no longer in
// the state the page was showing (its status changed elsewhere). The forms
// respond by refetching the work order so the right actions are offered again.
export function isStaleWorkOrderError(error) {
  const code = getApiErrorCode(error?.errors)
  return (
    code === ERROR_CODE.INVALID_ORDER_STATUS_CHANGE ||
    code === ERROR_CODE.WORK_ORDER_ACTION_NOT_ALLOWED
  )
}
